import React from "react";
import { FileType, LinkType } from "../../types/baseTypes";
import { customSanitize } from "../../utils/customSanitize";
import { downloadFile } from "../../utils/downloadFile";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import styles from "./ExtendedLink.module.scss";
import { useAppContext } from "../../context/AppContext";
import { Button, ButtonTypes } from "../Button/Button";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG";

export type DowloadButtonType = {
  link_target: FileType | false;
  link_text: string;
};

export type ExtendedLinkType = {
  type: "email" | "link" | "download" | "popup" | "telephone";
  link: LinkType;
  download: DowloadButtonType;
  email: {
    link_target: string;
    link_text: string;
  };
  pop_up: {
    link_text: string;
    link_aria_label: string;
  };
  telephone: {
    link_target: string;
    link_text: string;
    link_aria_label: string;
  };
};

interface ExtededLinkProps {
  extendedLink: ExtendedLinkType;
  extraClass?: string;
  iconOnly?: boolean;
  btnType: ButtonTypes;
}

export const ExtendedLink = ({
  extendedLink,
  extraClass = "",
  iconOnly = false,
  btnType = "primary",
}: ExtededLinkProps) => {
  const { type, link, email, download, pop_up, telephone } = extendedLink;
  const { getIcon, getLabel } = useGlobalContext();
  const { setopenPopup } = useAppContext();
  const ariaLabelDownload =
    download?.link_target && download.link_target.url
      ? getLabel("arialabel_download_slider").replace(
          `"$filename"`,
          (download.link_target as FileType).filename,
        )
      : "";

  const renderButtonContent = (text: string) => (
    <span
      dangerouslySetInnerHTML={{
        __html: customSanitize(text),
      }}
    />
  );

  const handleDownload = (fileUrl: string) => {
    downloadFile(fileUrl).catch((e) => {
      console.error("Download failed:", e);
      // Handle error if needed
    });
  };

  const renderDownloadButton = () =>
    download?.link_target &&
    download.link_target.url && (
      <Button
        type={btnType}
        href=""
        target=""
        isDownload
        className={`${extraClass} ${styles["download-btn"]}`}
        aria-label={ariaLabelDownload}
        onClick={() => handleDownload((download.link_target as FileType).url)}
      >
        {renderButtonContent(download.link_text)}
      </Button>
    );

  const renderDownloadIcon = () =>
    download?.link_target &&
    download.link_target.url && (
      <button
        type="button"
        className={`${styles["download-link-icon"]} ${extraClass}`}
        aria-label={ariaLabelDownload}
        onClick={() => handleDownload((download.link_target as FileType).url)}
      >
        <CustomSVG
          src={getIcon("download")}
          wrapper="span"
          width={38}
          height={38}
          aria-hidden="true"
        />
      </button>
    );

  const renderPopupButton = () => (
    <Button
      type={btnType}
      href=""
      target=""
      className={extraClass}
      aria-label={pop_up?.link_aria_label}
      aria-haspopup="dialog"
      onClick={() => {
        setopenPopup(true);
      }}
    >
      {renderButtonContent(pop_up.link_text)}
    </Button>
  );

  const buttonMapping = {
    link: link && (
      <Button
        type={btnType}
        href={link.url}
        target={link.target}
        aria-label={link.aria_label || link.title}
        className={extraClass}
      >
        {renderButtonContent(link.title)}
      </Button>
    ),
    email: email && (
      <Button
        type={btnType}
        href={`mailto:${email.link_target}`}
        target="_blank"
        className={extraClass}
        aria-label={getLabel("email_title").replace(
          `"$emailadress"`,
          email.link_target,
        )}
      >
        {renderButtonContent(email.link_text)}
      </Button>
    ),
    download: iconOnly ? renderDownloadIcon() : renderDownloadButton(),
    popup: renderPopupButton(),
    telephone: telephone && (
      <Button
        type={btnType}
        href={`tel:${telephone.link_target}`}
        target="_blank"
        className={extraClass}
        aria-label={
          telephone.link_aria_label
            ? telephone.link_aria_label
            : getLabel("extended_link_telephone_aria_label").replace(
                `$$phonenumber`,
                telephone.link_target,
              )
        }
      >
        {renderButtonContent(telephone.link_text)}
      </Button>
    ),
  };

  return <>{buttonMapping[type]}</>;
};
